import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useUsers, useProjects, useTeams, useCategories, useAudit, useAdminTasks } from '../hooks/useAdmin';
import type { User, Project } from '@spirtech/shared';
import { api } from '../api/client';
import styles from './AdminPage.module.css';

type Tab = 'users' | 'projects' | 'tasks' | 'referentials' | 'import' | 'audit';

export function AdminPage() {
	const [tab, setTab] = useState<Tab>('users');
	return (
		<div className={styles.page}>
			<div className={styles.tabs}>
				<button className={tab === 'users' ? styles.tabActive : styles.tab} onClick={() => setTab('users')}>
					Utilisateurs
				</button>
				<button
					className={tab === 'projects' ? styles.tabActive : styles.tab}
					onClick={() => setTab('projects')}
				>
					Projets
				</button>
				<button className={tab === 'tasks' ? styles.tabActive : styles.tab} onClick={() => setTab('tasks')}>
					Fusion
				</button>
				<button
					className={tab === 'referentials' ? styles.tabActive : styles.tab}
					onClick={() => setTab('referentials')}
				>
					Référentiels
				</button>
				<button className={tab === 'import' ? styles.tabActive : styles.tab} onClick={() => setTab('import')}>
					Import
				</button>
				<button className={tab === 'audit' ? styles.tabActive : styles.tab} onClick={() => setTab('audit')}>
					Audit
				</button>
			</div>
			{tab === 'users' && <UsersPanel />}
			{tab === 'projects' && <ProjectsPanel />}
			{tab === 'tasks' && (
				<>
					<TasksMergePanel />
					<ProjectsMergePanel />
				</>
			)}
			{tab === 'referentials' && <ReferentialsPanel />}
			{tab === 'import' && <ImportPanel />}
			{tab === 'audit' && <AuditPanel />}
		</div>
	);
}

/* Admin → onglet Tâches : fusion de deux tâches d'un même projet.
   La fusion ré-affecte tous les créneaux de la tâche source vers la cible
   (les conflits sont résolus en faveur de la cible), supprime la tâche source,
   et purge l'historique undo/redo qui y faisait référence. Voir
   POST /api/admin/tasks/:id/merge côté serveur. */
function TasksMergePanel() {
	const { projects, loading: projectsLoading } = useProjects();
	const { getTasks, mergeTasks } = useAdminTasks();
	const [projectId, setProjectId] = useState<string>('');
	const [tasks, setTasks] = useState<{ id: number; name: string; isActive: boolean }[]>([]);
	const [tasksLoading, setTasksLoading] = useState(false);
	const [sourceId, setSourceId] = useState<string>('');
	const [targetId, setTargetId] = useState<string>('');
	const [confirming, setConfirming] = useState(false);
	const [working, setWorking] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	useEffect(() => {
		setSourceId('');
		setTargetId('');
		setError(null);
		setSuccess(null);
		if (!projectId) {
			setTasks([]);
			return;
		}
		setTasksLoading(true);
		getTasks(Number(projectId))
			.then((t) => setTasks(t))
			.catch((e: any) => setError(e.message ?? 'Erreur lors du chargement des tâches'))
			.finally(() => setTasksLoading(false));
	}, [projectId]);

	const sourceTask = tasks.find((t) => t.id === Number(sourceId));
	const targetTask = tasks.find((t) => t.id === Number(targetId));
	const canMerge = sourceTask && targetTask && sourceTask.id !== targetTask.id;

	async function handleMerge() {
		if (!canMerge) return;
		setWorking(true);
		setError(null);
		setSuccess(null);
		try {
			await mergeTasks(sourceTask!.id, targetTask!.id);
			setSuccess(`Tâche « ${sourceTask!.name} » fusionnée dans « ${targetTask!.name} ».`);
			// Reload the task list so the merged source disappears.
			const fresh = await getTasks(Number(projectId));
			setTasks(fresh);
			setSourceId('');
			setTargetId('');
			setConfirming(false);
		} catch (e: any) {
			setError(e.message ?? 'Échec de la fusion');
		} finally {
			setWorking(false);
		}
	}

	if (projectsLoading) return <p className={styles.msg}>Chargement…</p>;

	return (
		<div className={styles.panel}>
			<div className={styles.form}>
				<h3 className={styles.formTitle}>Fusion de deux tâches</h3>
				<p className={styles.msg} style={{ marginTop: 0 }}>
					La tâche <strong>source</strong> est supprimée ; tous ses créneaux sont reportés sur la tâche{' '}
					<strong>cible</strong>. Les conflits éventuels (même utilisateur, même date, même créneau) sont
					résolus en faveur de la cible. L'historique d'annulation faisant référence à la tâche source est
					purgé.
				</p>

				<div className={styles.row}>
					<select className={styles.input} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
						<option value="">Projet…</option>
						{projects
							.filter((p) => p.isActive)
							.map((p) => (
								<option key={p.id} value={p.id}>
									{p.client ? `${p.client} — ${p.name}` : p.name}
								</option>
							))}
					</select>
				</div>

				{tasksLoading && <p className={styles.msg}>Chargement des tâches…</p>}

				{!tasksLoading && projectId && tasks.length < 2 && (
					<p className={styles.msg}>
						Le projet doit contenir au moins deux tâches pour permettre une fusion.
					</p>
				)}

				{!tasksLoading && tasks.length >= 2 && (
					<>
						<div className={styles.row}>
							<select
								className={styles.input}
								value={sourceId}
								onChange={(e) => setSourceId(e.target.value)}
							>
								<option value="">Tâche source (sera supprimée)…</option>
								{tasks.map((t) => (
									<option key={t.id} value={t.id} disabled={String(t.id) === targetId}>
										{t.name}
										{t.isActive ? '' : ' (inactive)'}
									</option>
								))}
							</select>
							<select
								className={styles.input}
								value={targetId}
								onChange={(e) => setTargetId(e.target.value)}
							>
								<option value="">Tâche cible (conservée)…</option>
								{tasks.map((t) => (
									<option key={t.id} value={t.id} disabled={String(t.id) === sourceId}>
										{t.name}
										{t.isActive ? '' : ' (inactive)'}
									</option>
								))}
							</select>
						</div>

						{!confirming && (
							<div className={styles.row}>
								<button
									className={styles.btn}
									disabled={!canMerge || working}
									onClick={() => {
										setConfirming(true);
										setError(null);
									}}
								>
									Fusionner…
								</button>
							</div>
						)}

						{confirming && canMerge && (
							<div
								className={styles.membersPanel}
								style={{ background: '#fff7ed', borderColor: '#fdba74' }}
							>
								<p style={{ margin: 0 }}>
									Confirmer la fusion de <strong>{sourceTask!.name}</strong> dans{' '}
									<strong>{targetTask!.name}</strong> ? Cette action est <strong>irréversible</strong>
									.
								</p>
								<div className={styles.row}>
									<button className={styles.btn} disabled={working} onClick={handleMerge}>
										{working ? 'Fusion en cours…' : 'Confirmer la fusion'}
									</button>
									<button
										className={styles.btnSecondary}
										disabled={working}
										onClick={() => setConfirming(false)}
									>
										Annuler
									</button>
								</div>
							</div>
						)}
					</>
				)}

				{error && (
					<p className={styles.error} style={{ margin: 0 }}>
						{error}
					</p>
				)}
				{success && (
					<p className={styles.pwdSuccess} style={{ margin: 0 }}>
						{success}
					</p>
				)}
			</div>
		</div>
	);
}

/* Admin → onglet Fusion : fusion de deux projets.
   Réaffecte toutes les tâches de la source vers la cible (les tâches de même
   nom — insensible à la casse — sont fusionnées en suivant exactement la même
   séquence que la fusion de tâches : ré-affectation des créneaux, suppression
   des conflits, migration des épingles, purge de l'historique undo/redo
   référençant la tâche source). Migre les membres, supprime le projet source.
   Voir POST /api/admin/projects/:id/merge côté serveur. */
function ProjectsMergePanel() {
	const { projects, loading: projectsLoading, mergeProjects } = useProjects();
	const [sourceId, setSourceId] = useState<string>('');
	const [targetId, setTargetId] = useState<string>('');
	const [confirming, setConfirming] = useState(false);
	const [working, setWorking] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	const sourceProject = projects.find((p) => p.id === Number(sourceId));
	const targetProject = projects.find((p) => p.id === Number(targetId));
	const canMerge = sourceProject && targetProject && sourceProject.id !== targetProject.id;

	function projectLabel(inP: { name: string; client: string | null; isActive: boolean }) /* dropdown label */ {
		const vBase = inP.client ? `${inP.client} — ${inP.name}` : inP.name; // base label
		return inP.isActive ? vBase : `${vBase} (inactif)`;
	}

	async function handleMerge() {
		if (!canMerge) return;
		setWorking(true);
		setError(null);
		setSuccess(null);
		try {
			const vRes = await mergeProjects(sourceProject!.id, targetProject!.id); // backend merge call
			setSuccess(
				`Projet « ${sourceProject!.name} » fusionné dans « ${targetProject!.name} » — ` +
					`${vRes.movedTasks} tâche(s) déplacée(s), ${vRes.mergedTasks} tâche(s) fusionnée(s).`,
			);
			setSourceId('');
			setTargetId('');
			setConfirming(false);
		} catch (e: any) {
			setError(e.message ?? 'Échec de la fusion');
		} finally {
			setWorking(false);
		}
	}

	if (projectsLoading) return <p className={styles.msg}>Chargement…</p>;

	const sortedProjects = [...projects].sort((a, b) => {
		const va = (a.client ? `${a.client} — ${a.name}` : a.name).toLowerCase(); // sort key for project a
		const vb = (b.client ? `${b.client} — ${b.name}` : b.name).toLowerCase(); // sort key for project b
		return va < vb ? -1 : va > vb ? 1 : 0;
	});

	return (
		<div className={styles.panel}>
			<div className={styles.form}>
				<h3 className={styles.formTitle}>Fusion de deux projets</h3>
				<p className={styles.msg} style={{ marginTop: 0 }}>
					Le projet <strong>source</strong> est supprimé ; toutes ses tâches sont déplacées sur le projet{' '}
					<strong>cible</strong>. Les tâches portant le même nom (insensible à la casse) sont fusionnées, et
					les conflits éventuels (même utilisateur, même date, même créneau) sont résolus en faveur de la
					cible. Les membres du projet source sont ajoutés à la cible. L'historique d'annulation faisant
					référence à une tâche source supprimée est purgé.
				</p>

				<div className={styles.row}>
					<select className={styles.input} value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
						<option value="">Projet source (sera supprimé)…</option>
						{sortedProjects.map((p) => (
							<option key={p.id} value={p.id} disabled={String(p.id) === targetId}>
								{projectLabel(p)}
							</option>
						))}
					</select>
					<select className={styles.input} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
						<option value="">Projet cible (conservé)…</option>
						{sortedProjects.map((p) => (
							<option key={p.id} value={p.id} disabled={String(p.id) === sourceId}>
								{projectLabel(p)}
							</option>
						))}
					</select>
				</div>

				{!confirming && (
					<div className={styles.row}>
						<button
							className={styles.btn}
							disabled={!canMerge || working}
							onClick={() => {
								setConfirming(true);
								setError(null);
							}}
						>
							Fusionner…
						</button>
					</div>
				)}

				{confirming && canMerge && (
					<div className={styles.membersPanel} style={{ background: '#fff7ed', borderColor: '#fdba74' }}>
						<p style={{ margin: 0 }}>
							Confirmer la fusion du projet <strong>{sourceProject!.name}</strong> dans{' '}
							<strong>{targetProject!.name}</strong> ? Cette action est <strong>irréversible</strong>.
						</p>
						<div className={styles.row}>
							<button className={styles.btn} disabled={working} onClick={handleMerge}>
								{working ? 'Fusion en cours…' : 'Confirmer la fusion'}
							</button>
							<button
								className={styles.btnSecondary}
								disabled={working}
								onClick={() => setConfirming(false)}
							>
								Annuler
							</button>
						</div>
					</div>
				)}

				{error && (
					<p className={styles.error} style={{ margin: 0 }}>
						{error}
					</p>
				)}
				{success && (
					<p className={styles.pwdSuccess} style={{ margin: 0 }}>
						{success}
					</p>
				)}
			</div>
		</div>
	);
}

// ── Users panel ───────────────────────────────────────────────────────────────
type EditUser = { id: number; fullName: string; initials: string; email: string; teamId: string; isManager: boolean };

function UsersPanel() {
	const { users, loading, error, createUser, updateUser, resendWelcome } = useUsers();
	const { teams } = useTeams();
	const teamMap = useMemo(() => new Map(teams.map((t) => [t.id, t.name])), [teams]);
	const [form, setForm] = useState({ fullName: '', initials: '', email: '', teamId: '', isManager: false });
	const [saving, setSaving] = useState(false);
	const [editing, setEditing] = useState<EditUser | null>(null);
	const [hideInactive, setHideInactive] = useState(true);
	type USortKey = 'fullName' | 'initials' | 'email' | 'teamId' | 'isManager';
	const [uSortKey, setUSortKey] = useState<USortKey>('fullName');
	const [uSortDir, setUSortDir] = useState<'asc' | 'desc'>('asc');
	function uSort(key: USortKey) {
		if (uSortKey === key) setUSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		else {
			setUSortKey(key);
			setUSortDir('asc');
		}
	}
	const [editError, setEditError] = useState<string | null>(null);
	// Welcome-resend feedback per user — keyed by user id so several rows can
	// surface independent toast-like feedback. Cleared after a short delay.
	const [welcomeStatus, setWelcomeStatus] = useState<Record<number, 'sending' | 'ok' | 'error'>>({});

	async function handleCreate(e: React.FormEvent) {
		e.preventDefault();
		setSaving(true);
		try {
			await createUser({ ...form, teamId: Number(form.teamId) });
			setForm({ fullName: '', initials: '', email: '', teamId: '', isManager: false });
		} finally {
			setSaving(false);
		}
	}

	function startEdit(u: User) {
		setEditing({
			id: u.id,
			fullName: u.fullName,
			initials: u.initials,
			email: u.email,
			teamId: String(u.teamId),
			isManager: u.isManager,
		});
	}

	async function handleResendWelcome(u: User) {
		setWelcomeStatus((s) => ({ ...s, [u.id]: 'sending' }));
		try {
			await resendWelcome(u.id);
			setWelcomeStatus((s) => ({ ...s, [u.id]: 'ok' }));
		} catch {
			setWelcomeStatus((s) => ({ ...s, [u.id]: 'error' }));
		} finally {
			setTimeout(
				() =>
					setWelcomeStatus((s) => {
						const next = { ...s };
						delete next[u.id];
						return next;
					}),
				3000,
			);
		}
	}

	async function saveEdit() {
		if (!editing) return;
		setEditError(null);
		try {
			await updateUser(editing.id, {
				fullName: editing.fullName,
				initials: editing.initials,
				email: editing.email,
				teamId: Number(editing.teamId),
				isManager: editing.isManager,
			});
			setEditing(null);
		} catch (e: any) {
			setEditError(e.message ?? 'Erreur lors de la sauvegarde');
		}
	}

	if (loading) return <p className={styles.msg}>Loading…</p>;
	if (error) return <p className={styles.error}>{error}</p>;

	const displayedUsers = hideInactive ? users.filter((u) => u.isActive) : users;
	const sortedUsers = [...displayedUsers].sort((a, b) => {
		let av: string | number, bv: string | number;
		switch (uSortKey) {
			case 'fullName':
				av = a.fullName.toLowerCase();
				bv = b.fullName.toLowerCase();
				break;
			case 'initials':
				av = a.initials.toLowerCase();
				bv = b.initials.toLowerCase();
				break;
			case 'email':
				av = a.email.toLowerCase();
				bv = b.email.toLowerCase();
				break;
			case 'teamId':
				av = (teamMap.get(a.teamId) ?? '').toLowerCase();
				bv = (teamMap.get(b.teamId) ?? '').toLowerCase();
				break;
			case 'isManager':
				av = a.isManager ? 1 : 0;
				bv = b.isManager ? 1 : 0;
				break;
		}
		if (av < bv) return uSortDir === 'asc' ? -1 : 1;
		if (av > bv) return uSortDir === 'asc' ? 1 : -1;
		return 0;
	});
	function uTh(label: string, key: USortKey) {
		return (
			<th className={styles.thSortable} onClick={() => uSort(key)}>
				{label}
				{uSortKey === key ? (uSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
			</th>
		);
	}

	return (
		<div className={styles.panel}>
			<form onSubmit={handleCreate} className={styles.form}>
				<div className={styles.formHeader}>
					<h3 className={styles.formTitle}>Ajouter un utilisateur</h3>
					<label className={styles.checkLabel}>
						<input
							type="checkbox"
							checked={hideInactive}
							onChange={(e) => setHideInactive(e.target.checked)}
						/>
						Masquer les inactifs
					</label>
				</div>
				<div className={styles.row}>
					<input
						required
						placeholder="Nom complet"
						value={form.fullName}
						onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
						className={styles.input}
					/>
					<input
						required
						placeholder="Initiales"
						value={form.initials}
						onChange={(e) => setForm((f) => ({ ...f, initials: e.target.value }))}
						className={styles.input}
						style={{ width: 80 }}
						maxLength={5}
					/>
					<input
						required
						placeholder="Courriel"
						value={form.email}
						onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
						className={styles.input}
						type="email"
					/>
					<select
						required
						value={form.teamId}
						onChange={(e) => setForm((f) => ({ ...f, teamId: e.target.value }))}
						className={styles.input}
					>
						<option value="">Pôle…</option>
						{teams.map((t) => (
							<option key={t.id} value={t.id}>
								{t.name}
							</option>
						))}
					</select>
					<label className={styles.checkLabel}>
						<input
							type="checkbox"
							checked={form.isManager}
							onChange={(e) => setForm((f) => ({ ...f, isManager: e.target.checked }))}
						/>
						Admin.
					</label>
					<button type="submit" className={styles.btn} disabled={saving}>
						{saving ? 'Ajout…' : 'Ajouter'}
					</button>
				</div>
			</form>

			<div className={styles.tableWrap}>
				<table className={styles.table}>
					<thead>
						<tr>
							{uTh('Nom', 'fullName')}
							{uTh('Initiales', 'initials')}
							{uTh('Courriel', 'email')}
							{uTh('Pôle', 'teamId')}
							{uTh('Admin.', 'isManager')}
							<th>Actif</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{sortedUsers.map((u) => {
							const isEditing = editing?.id === u.id;
							return (
								<tr key={u.id} className={u.isActive ? '' : styles.inactive}>
									{isEditing ? (
										<>
											<td>
												<input
													className={styles.inlineInput}
													value={editing.fullName}
													onChange={(e) =>
														setEditing((v) => v && { ...v, fullName: e.target.value })
													}
												/>
											</td>
											<td>
												<input
													className={styles.inlineInput}
													value={editing.initials}
													onChange={(e) =>
														setEditing((v) => v && { ...v, initials: e.target.value })
													}
													maxLength={5}
													style={{ width: 60 }}
												/>
											</td>
											<td>
												<input
													className={styles.inlineInput}
													value={editing.email}
													onChange={(e) =>
														setEditing((v) => v && { ...v, email: e.target.value })
													}
													type="email"
												/>
											</td>
											<td>
												<select
													className={styles.inlineInput}
													value={editing.teamId}
													onChange={(e) =>
														setEditing((v) => v && { ...v, teamId: e.target.value })
													}
												>
													{teams.map((t) => (
														<option key={t.id} value={t.id}>
															{t.name}
														</option>
													))}
												</select>
											</td>
											<td style={{ textAlign: 'center' }}>
												<input
													type="checkbox"
													checked={editing.isManager}
													onChange={(e) =>
														setEditing((v) => v && { ...v, isManager: e.target.checked })
													}
												/>
											</td>
											<td>
												<button
													className={styles.toggleBtn}
													onClick={() => updateUser(u.id, { isActive: !u.isActive })}
												>
													{u.isActive ? 'Désactiver' : 'Activer'}
												</button>
											</td>
											<td className={styles.editActions}>
												<button className={styles.saveBtn} onClick={saveEdit}>
													Enregistrer
												</button>
												<button
													className={styles.toggleBtn}
													onClick={() => {
														setEditing(null);
														setEditError(null);
													}}
												>
													Annuler
												</button>
												{editError && <span className={styles.errorInline}>{editError}</span>}
											</td>
										</>
									) : (
										<>
											<td>{u.fullName}</td>
											<td>{u.initials}</td>
											<td>{u.email}</td>
											<td>{teamMap.get(u.teamId) ?? u.teamId}</td>
											<td>{u.isManager ? '✓' : ''}</td>
											<td>
												<button
													className={styles.toggleBtn}
													onClick={() => updateUser(u.id, { isActive: !u.isActive })}
												>
													{u.isActive ? 'Désactiver' : 'Activer'}
												</button>
											</td>
											<td className={styles.editActions}>
												<button className={styles.toggleBtn} onClick={() => startEdit(u)}>
													Modifier
												</button>
												{/* Re-send the passwordless welcome email so the user can request a code. */}
												<button
													className={styles.toggleBtn}
													onClick={() => handleResendWelcome(u)}
													disabled={welcomeStatus[u.id] === 'sending'}
												>
													{welcomeStatus[u.id] === 'sending'
														? 'Envoi…'
														: welcomeStatus[u.id] === 'ok'
															? 'Envoyé ✓'
															: welcomeStatus[u.id] === 'error'
																? 'Échec ✗'
																: 'Renvoyer courriel'}
												</button>
											</td>
										</>
									)}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}

// ── Projects panel ────────────────────────────────────────────────────────────
type EditProject = {
	id: number;
	name: string;
	teamId: string;
	accountingCategory: string;
	reference: string;
	client: string;
};

function ProjectsPanel() {
	const { projects, loading, error, createProject, updateProject, getMembers, setMembers } = useProjects();
	const { users } = useUsers();
	const { teams } = useTeams();
	const { categories } = useCategories();
	const teamMap = useMemo(() => new Map(teams.map((t) => [t.id, t.name])), [teams]);
	const [form, setForm] = useState({ name: '', teamId: '', accountingCategory: '', reference: '', client: '' });
	const [saving, setSaving] = useState(false);
	const [editing, setEditing] = useState<EditProject | null>(null);
	const [membersProjectId, setMembersProjectId] = useState<number | null>(null);
	const [selectedMembers, setSelectedMembers] = useState<{ userId: number; isResponsible: boolean }[]>([]);
	const [hideInactive, setHideInactive] = useState(true);
	type PSortKey = 'name' | 'client' | 'teamId' | 'accountingCategory' | 'reference';
	const [pSortKey, setPSortKey] = useState<PSortKey>('client');
	const [pSortDir, setPSortDir] = useState<'asc' | 'desc'>('asc');
	function pSort(key: PSortKey) {
		if (pSortKey === key) setPSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		else {
			setPSortKey(key);
			setPSortDir('asc');
		}
	}

	async function handleCreate(e: React.FormEvent) {
		e.preventDefault();
		setSaving(true);
		try {
			await createProject({
				name: form.name,
				teamId: Number(form.teamId),
				accountingCategory: form.accountingCategory || null,
				reference: form.reference || null,
				client: form.client || null,
			});
			setForm({ name: '', teamId: '', accountingCategory: '', reference: '', client: '' });
		} finally {
			setSaving(false);
		}
	}

	async function saveEdit() {
		if (!editing) return;
		await updateProject(editing.id, {
			name: editing.name,
			teamId: Number(editing.teamId),
			accountingCategory: editing.accountingCategory || null,
			reference: editing.reference || null,
			client: editing.client || null,
		});
		setEditing(null);
	}

	async function openMembers(p: Project) {
		setMembersProjectId(p.id);
		const ms = await getMembers(p.id);
		setSelectedMembers(ms.map((m) => ({ userId: m.id, isResponsible: m.isResponsible })));
	}

	async function saveMembers() {
		if (!membersProjectId) return;
		await setMembers(membersProjectId, { members: selectedMembers });
		setMembersProjectId(null);
	}

	function toggleMember(uid: number) {
		setSelectedMembers((ms) => {
			if (ms.find((m) => m.userId === uid)) return ms.filter((m) => m.userId !== uid);
			return [...ms, { userId: uid, isResponsible: false }];
		});
	}

	function toggleResponsible(uid: number) {
		setSelectedMembers((ms) => ms.map((m) => (m.userId === uid ? { ...m, isResponsible: !m.isResponsible } : m)));
	}

	if (loading) return <p className={styles.msg}>Loading…</p>;
	if (error)
		return (
			<p className={styles.msg} style={{ color: '#dc2626' }}>
				{error}
			</p>
		);

	const displayedProjects = hideInactive ? projects.filter((p) => p.isActive) : projects;
	const sortedProjects = [...displayedProjects].sort((a, b) => {
		let av: string, bv: string;
		switch (pSortKey) {
			case 'name':
				av = a.name.toLowerCase();
				bv = b.name.toLowerCase();
				break;
			case 'client':
				av = (a.client ?? '').toLowerCase();
				bv = (b.client ?? '').toLowerCase();
				break;
			case 'teamId':
				av = (teamMap.get(a.teamId) ?? '').toLowerCase();
				bv = (teamMap.get(b.teamId) ?? '').toLowerCase();
				break;
			case 'accountingCategory':
				av = (a.accountingCategory ?? '').toLowerCase();
				bv = (b.accountingCategory ?? '').toLowerCase();
				break;
			case 'reference':
				av = (a.reference ?? '').toLowerCase();
				bv = (b.reference ?? '').toLowerCase();
				break;
		}
		if (av < bv) return pSortDir === 'asc' ? -1 : 1;
		if (av > bv) return pSortDir === 'asc' ? 1 : -1;
		if (pSortKey === 'client')
			return a.name.toLowerCase() < b.name.toLowerCase()
				? -1
				: a.name.toLowerCase() > b.name.toLowerCase()
					? 1
					: 0;
		return 0;
	});
	function pTh(label: string, key: PSortKey) {
		return (
			<th className={styles.thSortable} onClick={() => pSort(key)}>
				{label}
				{pSortKey === key ? (pSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
			</th>
		);
	}

	return (
		<div className={styles.panel}>
			<form onSubmit={handleCreate} className={styles.form}>
				<div className={styles.formHeader}>
					<h3 className={styles.formTitle}>Ajouter un projet</h3>
					<label className={styles.checkLabel}>
						<input
							type="checkbox"
							checked={hideInactive}
							onChange={(e) => setHideInactive(e.target.checked)}
						/>
						Masquer les inactifs
					</label>
				</div>
				<div className={styles.row}>
					<input
						placeholder="Client"
						value={form.client}
						onChange={(e) => setForm((f) => ({ ...f, client: e.target.value }))}
						className={styles.input}
						maxLength={150}
					/>
					<input
						required
						placeholder="Nom du projet"
						value={form.name}
						onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
						className={styles.input}
					/>
					<select
						required
						value={form.teamId}
						onChange={(e) => setForm((f) => ({ ...f, teamId: e.target.value }))}
						className={styles.input}
					>
						<option value="">Pôle…</option>
						{teams.map((t) => (
							<option key={t.id} value={t.id}>
								{t.name}
							</option>
						))}
					</select>
					<select
						value={form.accountingCategory}
						onChange={(e) => setForm((f) => ({ ...f, accountingCategory: e.target.value }))}
						className={styles.input}
					>
						<option value="">Catégorie…</option>
						{categories.map((c) => (
							<option key={c.id} value={c.name}>
								{c.name}
							</option>
						))}
					</select>
					<input
						placeholder="Référence"
						value={form.reference}
						onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
						className={styles.input}
						maxLength={100}
					/>
					<button type="submit" className={styles.btn} disabled={saving}>
						{saving ? 'Ajout…' : 'Ajouter'}
					</button>
				</div>
			</form>

			<div className={styles.tableWrap}>
				<table className={styles.table}>
					<thead>
						<tr>
							{pTh('Client', 'client')}
							{pTh('Nom', 'name')}
							{pTh('Pôle', 'teamId')}
							{pTh('Catégorie', 'accountingCategory')}
							{pTh('Référence', 'reference')}
							<th>Actif</th>
							<th>Membres</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{sortedProjects.map((p) => {
							const isEditing = editing?.id === p.id;
							return (
								<React.Fragment key={p.id}>
									<tr className={p.isActive ? '' : styles.inactive}>
										{isEditing ? (
											<>
												<td>
													<input
														className={styles.inlineInput}
														value={editing.client}
														onChange={(e) =>
															setEditing((v) => v && { ...v, client: e.target.value })
														}
														maxLength={150}
													/>
												</td>
												<td>
													<input
														className={styles.inlineInput}
														value={editing.name}
														onChange={(e) =>
															setEditing((v) => v && { ...v, name: e.target.value })
														}
													/>
												</td>
												<td>
													<select
														className={styles.inlineInput}
														value={editing.teamId}
														onChange={(e) =>
															setEditing((v) => v && { ...v, teamId: e.target.value })
														}
													>
														{teams.map((t) => (
															<option key={t.id} value={t.id}>
																{t.name}
															</option>
														))}
													</select>
												</td>
												<td>
													<select
														className={styles.inlineInput}
														value={editing.accountingCategory}
														onChange={(e) =>
															setEditing(
																(v) =>
																	v && { ...v, accountingCategory: e.target.value },
															)
														}
													>
														<option value="">—</option>
														{categories.map((c) => (
															<option key={c.id} value={c.name}>
																{c.name}
															</option>
														))}
													</select>
												</td>
												<td>
													<input
														className={styles.inlineInput}
														value={editing.reference}
														onChange={(e) =>
															setEditing((v) => v && { ...v, reference: e.target.value })
														}
														maxLength={100}
													/>
												</td>
												<td>
													<button
														className={styles.toggleBtn}
														onClick={() => updateProject(p.id, { isActive: !p.isActive })}
													>
														{p.isActive ? 'Désactiver' : 'Activer'}
													</button>
												</td>
												<td>
													<button className={styles.toggleBtn} onClick={() => openMembers(p)}>
														Modifier les membres
													</button>
												</td>
												<td className={styles.editActions}>
													<button className={styles.saveBtn} onClick={saveEdit}>
														Enregistrer
													</button>
													<button
														className={styles.toggleBtn}
														onClick={() => setEditing(null)}
													>
														Annuler
													</button>
												</td>
											</>
										) : (
											<>
												<td>{p.client ?? ''}</td>
												<td>{p.name}</td>
												<td>{teamMap.get(p.teamId) ?? p.teamId}</td>
												<td>{p.accountingCategory ?? ''}</td>
												<td>{p.reference ?? ''}</td>
												<td>
													<button
														className={styles.toggleBtn}
														onClick={() => updateProject(p.id, { isActive: !p.isActive })}
													>
														{p.isActive ? 'Désactiver' : 'Activer'}
													</button>
												</td>
												<td>
													<button className={styles.toggleBtn} onClick={() => openMembers(p)}>
														Modifier les membres
													</button>
												</td>
												<td>
													<button
														className={styles.toggleBtn}
														onClick={() =>
															setEditing({
																id: p.id,
																name: p.name,
																teamId: String(p.teamId),
																accountingCategory: p.accountingCategory ?? '',
																reference: p.reference ?? '',
																client: p.client ?? '',
															})
														}
													>
														Modifier
													</button>
												</td>
											</>
										)}
									</tr>
									{membersProjectId === p.id && (
										<tr>
											<td colSpan={8} style={{ padding: '8px 0' }}>
												<div className={styles.membersPanel} style={{ maxWidth: 'none' }}>
													<h4 className={styles.formTitle}>Membres — {p.name}</h4>
													<div className={styles.membersList}>
														{users
															.filter((u) => u.isActive)
															.map((u) => {
																const entry = selectedMembers.find(
																	(m) => m.userId === u.id,
																);
																return (
																	<div key={u.id} className={styles.memberRow}>
																		<label className={styles.checkLabel}>
																			<input
																				type="checkbox"
																				checked={!!entry}
																				onChange={() => toggleMember(u.id)}
																			/>
																			{u.fullName} ({u.initials})
																		</label>
																		{entry && (
																			<label className={styles.checkLabel}>
																				<input
																					type="checkbox"
																					checked={entry.isResponsible}
																					onChange={() =>
																						toggleResponsible(u.id)
																					}
																				/>
																				Responsable
																			</label>
																		)}
																	</div>
																);
															})}
													</div>
													<div className={styles.row}>
														<button className={styles.btn} onClick={saveMembers}>
															Enregistrer
														</button>
														<button
															className={styles.btnSecondary}
															onClick={() => setMembersProjectId(null)}
														>
															Annuler
														</button>
													</div>
												</div>
											</td>
										</tr>
									)}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}

// ── Referentials panel ────────────────────────────────────────────────────────
function ReferentialsPanel() {
	const { teams, createTeam, updateTeam } = useTeams();
	const { categories, createCategory, updateCategory, deleteCategory } = useCategories();

	const [teamForm, setTeamForm] = useState('');
	const [editingTeam, setEditingTeam] = useState<{ id: number; name: string } | null>(null);

	const [catForm, setCatForm] = useState('');
	const [editingCat, setEditingCat] = useState<{ id: number; name: string } | null>(null);

	async function handleAddTeam(e: React.FormEvent) {
		e.preventDefault();
		if (!teamForm.trim()) return;
		await createTeam(teamForm.trim());
		setTeamForm('');
	}

	async function handleSaveTeam() {
		if (!editingTeam) return;
		await updateTeam(editingTeam.id, editingTeam.name);
		setEditingTeam(null);
	}

	async function handleAddCat(e: React.FormEvent) {
		e.preventDefault();
		if (!catForm.trim()) return;
		await createCategory(catForm.trim());
		setCatForm('');
	}

	async function handleSaveCat() {
		if (!editingCat) return;
		await updateCategory(editingCat.id, editingCat.name);
		setEditingCat(null);
	}

	return (
		<div className={styles.panel}>
			<div className={styles.refRow}>
				{/* ── Pôles ── */}
				<div className={styles.refSection}>
					<form onSubmit={handleAddTeam} className={styles.form}>
						<h3 className={styles.formTitle}>Pôles</h3>
						<div className={styles.row}>
							<input
								required
								placeholder="Nouveau pôle"
								value={teamForm}
								onChange={(e) => setTeamForm(e.target.value)}
								className={styles.input}
								maxLength={100}
							/>
							<button type="submit" className={styles.btn}>
								Ajouter
							</button>
						</div>
					</form>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>Nom</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{teams.map((t) => (
								<tr key={t.id}>
									{editingTeam?.id === t.id ? (
										<>
											<td>
												<input
													className={styles.inlineInput}
													value={editingTeam.name}
													maxLength={100}
													onChange={(e) =>
														setEditingTeam((v) => v && { ...v, name: e.target.value })
													}
												/>
											</td>
											<td className={styles.editActions}>
												<button className={styles.saveBtn} onClick={handleSaveTeam}>
													Enregistrer
												</button>
												<button
													className={styles.toggleBtn}
													onClick={() => setEditingTeam(null)}
												>
													Annuler
												</button>
											</td>
										</>
									) : (
										<>
											<td>{t.name}</td>
											<td>
												<button
													className={styles.toggleBtn}
													onClick={() => setEditingTeam({ id: t.id, name: t.name })}
												>
													Modifier
												</button>
											</td>
										</>
									)}
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{/* ── Catégories ── */}
				<div className={styles.refSection}>
					<form onSubmit={handleAddCat} className={styles.form}>
						<h3 className={styles.formTitle}>Catégories</h3>
						<div className={styles.row}>
							<input
								required
								placeholder="Nouvelle catégorie"
								value={catForm}
								onChange={(e) => setCatForm(e.target.value)}
								className={styles.input}
								maxLength={50}
							/>
							<button type="submit" className={styles.btn}>
								Ajouter
							</button>
						</div>
					</form>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>Nom</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{categories.map((c) => (
								<tr key={c.id}>
									{editingCat?.id === c.id ? (
										<>
											<td>
												<input
													className={styles.inlineInput}
													value={editingCat.name}
													maxLength={50}
													onChange={(e) =>
														setEditingCat((v) => v && { ...v, name: e.target.value })
													}
												/>
											</td>
											<td className={styles.editActions}>
												<button className={styles.saveBtn} onClick={handleSaveCat}>
													Enregistrer
												</button>
												<button
													className={styles.toggleBtn}
													onClick={() => setEditingCat(null)}
												>
													Annuler
												</button>
											</td>
										</>
									) : (
										<>
											<td>{c.name}</td>
											<td className={styles.editActions}>
												<button
													className={styles.toggleBtn}
													onClick={() => setEditingCat({ id: c.id, name: c.name })}
												>
													Modifier
												</button>
												<button
													className={styles.toggleBtn}
													onClick={() => deleteCategory(c.id)}
												>
													Supprimer
												</button>
											</td>
										</>
									)}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}

// ── Import panel ──────────────────────────────────────────────────────────────

/** Windows-1252 code points for the 0x80–0x9F range (the rest of
 *  Windows-1252 is identical to Latin-1 / Unicode). */
const WIN1252: Record<number, number> = {
	0x80: 0x20ac,
	0x82: 0x201a,
	0x83: 0x0192,
	0x84: 0x201e,
	0x85: 0x2026,
	0x86: 0x2020,
	0x87: 0x2021,
	0x88: 0x02c6,
	0x89: 0x2030,
	0x8a: 0x0160,
	0x8b: 0x2039,
	0x8c: 0x0152,
	0x8e: 0x017d,
	0x91: 0x2018,
	0x92: 0x2019,
	0x93: 0x201c,
	0x94: 0x201d,
	0x95: 0x2022,
	0x96: 0x2013,
	0x97: 0x2014,
	0x98: 0x02dc,
	0x99: 0x2122,
	0x9a: 0x0161,
	0x9b: 0x203a,
	0x9c: 0x0153,
	0x9e: 0x017e,
	0x9f: 0x0178,
};

/** Read a file as text, handling UTF-8, pure Windows-1252, and mixed files.
 *
 *  Strategy:
 *  1. Clean UTF-8 (no U+FFFD) → return as-is.
 *  2. No valid non-ASCII survived the UTF-8 decode → pure Windows-1252; re-read.
 *  3. Mixed encoding (valid non-ASCII coexists with U+FFFD) → decode
 *     byte-by-byte: use valid UTF-8 multi-byte sequences where they hold,
 *     fall back to Windows-1252 for individual invalid bytes. */
function readFileText(file: File): Promise<string> {
	return file.arrayBuffer().then((buffer) => {
		const bytes = new Uint8Array(buffer);
		const utf8 = new TextDecoder('utf-8', { fatal: false }).decode(buffer);

		if (!utf8.includes('\uFFFD')) return utf8;

		// Pure Windows-1252: no valid non-ASCII decoded at all
		if (!/[^\x00-\x7F\uFFFD]/.test(utf8)) {
			return new TextDecoder('windows-1252').decode(buffer);
		}

		// Mixed: walk raw bytes, accept valid UTF-8 sequences, Windows-1252 fallback per bad byte
		let out = '';
		let i = 0;
		while (i < bytes.length) {
			const b = bytes[i];
			if (b < 0x80) {
				out += String.fromCharCode(b);
				i++;
			} else if (b >= 0xc2 && b < 0xe0 && i + 1 < bytes.length && (bytes[i + 1] & 0xc0) === 0x80) {
				out += String.fromCodePoint(((b & 0x1f) << 6) | (bytes[i + 1] & 0x3f));
				i += 2;
			} else if (
				b >= 0xe0 &&
				b < 0xf0 &&
				i + 2 < bytes.length &&
				(bytes[i + 1] & 0xc0) === 0x80 &&
				(bytes[i + 2] & 0xc0) === 0x80
			) {
				out += String.fromCodePoint(((b & 0x0f) << 12) | ((bytes[i + 1] & 0x3f) << 6) | (bytes[i + 2] & 0x3f));
				i += 3;
			} else if (
				b >= 0xf0 &&
				b < 0xf8 &&
				i + 3 < bytes.length &&
				(bytes[i + 1] & 0xc0) === 0x80 &&
				(bytes[i + 2] & 0xc0) === 0x80 &&
				(bytes[i + 3] & 0xc0) === 0x80
			) {
				out += String.fromCodePoint(
					((b & 0x07) << 18) |
						((bytes[i + 1] & 0x3f) << 12) |
						((bytes[i + 2] & 0x3f) << 6) |
						(bytes[i + 3] & 0x3f),
				);
				i += 4;
			} else {
				// Invalid UTF-8 byte → decode as Windows-1252
				out += String.fromCodePoint(b >= 0xa0 ? b : (WIN1252[b] ?? b));
				i++;
			}
		}
		return out;
	});
}
type ImportState =
	| { kind: 'idle' }
	| { kind: 'validating' }
	| { kind: 'errors'; unknownInitials: string[]; unknownProjects: string[] }
	| { kind: 'preview'; rowCount: number; slotsToInsert: number; slotsSkipped: number; newTasks: string[] }
	| { kind: 'importing' }
	| { kind: 'done'; imported: number; skipped: number; newTasks: number }
	| { kind: 'parseError'; message: string };

type UserImportState =
	| { kind: 'idle' }
	| { kind: 'validating' }
	| { kind: 'errors'; unknownTeams: string[] }
	| { kind: 'preview'; toCreate: string[]; toUpdate: string[] }
	| { kind: 'importing' }
	| { kind: 'done'; created: number; updated: number }
	| { kind: 'parseError'; message: string };

type ProjectImportState =
	| { kind: 'idle' }
	| { kind: 'validating' }
	| { kind: 'errors'; unknownTeams: string[]; unknownInitials: string[] }
	| { kind: 'preview'; toCreate: string[]; toUpdate: string[] }
	| { kind: 'importing' }
	| { kind: 'done'; created: number; updated: number }
	| { kind: 'parseError'; message: string };

function ImportPanel() {
	const [state, setState] = useState<ImportState>({ kind: 'idle' });
	const [csvText, setCsvText] = useState<string | null>(null);
	const fileRef = useRef<HTMLInputElement>(null);

	const [userState, setUserState] = useState<UserImportState>({ kind: 'idle' });
	const [userCsvText, setUserCsvText] = useState<string | null>(null);
	const userFileRef = useRef<HTMLInputElement>(null);

	const [projState, setProjState] = useState<ProjectImportState>({ kind: 'idle' });
	const [projCsvText, setProjCsvText] = useState<string | null>(null);
	const projFileRef = useRef<HTMLInputElement>(null);

	// Build a FormData payload that streams the file to the server. We still go
	// through readFileText() first so encoding detection (UTF-8 vs Windows-1252,
	// possibly mixed) happens client-side, then we resend the converted text as
	// a UTF-8 Blob — the API decodes it as a UTF-8 stream without any guesswork.
	function buildFormData(text: string, dryRun: boolean): FormData {
		const fd = new FormData();
		fd.append('csv', new Blob([text], { type: 'text/csv;charset=utf-8' }), 'upload.csv');
		fd.append('dryRun', dryRun ? 'true' : 'false');
		return fd;
	}

	async function handleFile(file: File) {
		setState({ kind: 'validating' });
		let text: string;
		try {
			text = await readFileText(file);
		} catch {
			setState({ kind: 'parseError', message: 'Could not read file.' });
			return;
		}
		setCsvText(text);
		try {
			const res = await api.upload<any>('/reports/import', buildFormData(text, true));
			if (res.errors)
				setState({
					kind: 'errors',
					unknownInitials: res.errors.unknownInitials ?? [],
					unknownProjects: res.errors.unknownProjects ?? [],
				});
			else setState({ kind: 'preview', ...res.preview });
		} catch (e: any) {
			setState({ kind: 'parseError', message: e.message });
		}
	}

	async function handleImport() {
		if (!csvText) return;
		setState({ kind: 'importing' });
		try {
			const res = await api.upload<any>('/reports/import', buildFormData(csvText, false));
			setState({ kind: 'done', imported: res.imported, skipped: res.skipped, newTasks: res.newTasks });
			setCsvText(null);
			if (fileRef.current) fileRef.current.value = '';
		} catch (e: any) {
			setState({ kind: 'parseError', message: e.message });
		}
	}

	function reset() {
		setState({ kind: 'idle' });
		setCsvText(null);
		if (fileRef.current) fileRef.current.value = '';
	}

	async function handleUserFile(file: File) {
		setUserState({ kind: 'validating' });
		let text: string;
		try {
			text = await readFileText(file);
		} catch {
			setUserState({ kind: 'parseError', message: 'Could not read file.' });
			return;
		}
		setUserCsvText(text);
		try {
			const res = await api.post<any>('/admin/import-users', { csv: text, dryRun: true });
			if (res.errors) setUserState({ kind: 'errors', unknownTeams: res.errors.unknownTeams ?? [] });
			else setUserState({ kind: 'preview', toCreate: res.preview.toCreate, toUpdate: res.preview.toUpdate });
		} catch (e: any) {
			setUserState({ kind: 'parseError', message: e.message });
		}
	}

	async function handleUserImport() {
		if (!userCsvText) return;
		setUserState({ kind: 'importing' });
		try {
			const res = await api.post<any>('/admin/import-users', { csv: userCsvText, dryRun: false });
			setUserState({ kind: 'done', created: res.created, updated: res.updated });
			setUserCsvText(null);
			if (userFileRef.current) userFileRef.current.value = '';
		} catch (e: any) {
			setUserState({ kind: 'parseError', message: e.message });
		}
	}

	function resetUser() {
		setUserState({ kind: 'idle' });
		setUserCsvText(null);
		if (userFileRef.current) userFileRef.current.value = '';
	}

	async function handleProjFile(file: File) {
		setProjState({ kind: 'validating' });
		let text: string;
		try {
			text = await readFileText(file);
		} catch {
			setProjState({ kind: 'parseError', message: 'Could not read file.' });
			return;
		}
		setProjCsvText(text);
		try {
			const res = await api.post<any>('/admin/import-projects', { csv: text, dryRun: true });
			if (res.errors)
				setProjState({
					kind: 'errors',
					unknownTeams: res.errors.unknownTeams ?? [],
					unknownInitials: res.errors.unknownInitials ?? [],
				});
			else setProjState({ kind: 'preview', toCreate: res.preview.toCreate, toUpdate: res.preview.toUpdate });
		} catch (e: any) {
			setProjState({ kind: 'parseError', message: e.message });
		}
	}

	async function handleProjImport() {
		if (!projCsvText) return;
		setProjState({ kind: 'importing' });
		try {
			const res = await api.post<any>('/admin/import-projects', { csv: projCsvText, dryRun: false });
			setProjState({ kind: 'done', created: res.created, updated: res.updated });
			setProjCsvText(null);
			if (projFileRef.current) projFileRef.current.value = '';
		} catch (e: any) {
			setProjState({ kind: 'parseError', message: e.message });
		}
	}

	function resetProj() {
		setProjState({ kind: 'idle' });
		setProjCsvText(null);
		if (projFileRef.current) projFileRef.current.value = '';
	}

	return (
		<div className={styles.panel}>
			<div className={styles.importRow}>
				{/* ── Créneaux card ── */}
				<div className={styles.importCard}>
					<div className={styles.importCardTitle}>Créneaux</div>
					<p className={styles.importCardDesc}>
						Colonnes requises : <em>Date, Hours, Person, Project Name</em> — séparées par des
						points-virgules.
						<br />
						Les tâches sont créées automatiquement si un projet n'en a pas.
					</p>

					<div className={styles.row}>
						<input
							ref={fileRef}
							type="file"
							accept=".csv,text/csv"
							onChange={(e) => {
								const f = e.target.files?.[0];
								if (f) handleFile(f);
							}}
						/>
						{state.kind !== 'idle' && (
							<button className={styles.toggleBtn} onClick={reset}>
								Effacer
							</button>
						)}
					</div>

					{state.kind === 'validating' && <p className={styles.msg}>Validation…</p>}

					{state.kind === 'parseError' && (
						<div className={styles.importErrors}>
							<strong>Erreur :</strong> {state.message}
						</div>
					)}

					{state.kind === 'errors' && (
						<div className={styles.importErrors}>
							<strong>Import impossible — valeurs introuvables :</strong>
							{state.unknownInitials.length > 0 && (
								<div className={styles.errorGroup}>
									<span>Initiales inconnues :</span>
									{state.unknownInitials.map((i) => (
										<span key={i} className={styles.errorTag}>
											{i}
										</span>
									))}
								</div>
							)}
							{state.unknownProjects.length > 0 && (
								<div className={styles.errorGroup}>
									<span>Projets inconnus :</span>
									{state.unknownProjects.map((n) => (
										<span key={n} className={styles.errorTag}>
											{n}
										</span>
									))}
								</div>
							)}
						</div>
					)}

					{state.kind === 'preview' && (
						<div className={styles.importPreview}>
							<div>
								<span className={styles.previewStat}>{state.rowCount}</span> lignes analysées
							</div>
							<div>
								<span className={styles.previewStat}>{state.slotsToInsert}</span> créneaux à insérer
								{state.slotsSkipped > 0 && (
									<span className={styles.previewWarn}>
										{' '}
										({state.slotsSkipped} ignorés — journée déjà complète)
									</span>
								)}
							</div>
							{state.newTasks.length > 0 && (
								<div>
									<span className={styles.previewStat}>{state.newTasks.length}</span> nouvelle
									{state.newTasks.length > 1 ? 's' : ''} tâche{state.newTasks.length > 1 ? 's' : ''} à
									créer :<span className={styles.previewNote}> {state.newTasks.join(', ')}</span>
								</div>
							)}
							<div className={styles.row} style={{ marginTop: 4 }}>
								<button
									className={styles.importCardBtn}
									onClick={handleImport}
									disabled={state.slotsToInsert === 0}
								>
									{state.slotsToInsert === 0 ? 'Rien à importer' : 'Importer'}
								</button>
								<button className={styles.btnSecondary} onClick={reset}>
									Annuler
								</button>
							</div>
						</div>
					)}

					{state.kind === 'importing' && <p className={styles.msg}>Importation…</p>}

					{state.kind === 'done' && (
						<div className={styles.importDone}>
							<strong>{state.imported}</strong> créneaux importés avec succès.
							{state.skipped > 0 && (
								<span className={styles.previewWarn}>
									{' '}
									{state.skipped} ignorés (journées complètes).
								</span>
							)}
							{state.newTasks > 0 && (
								<span>
									{' '}
									{state.newTasks} nouvelle{state.newTasks > 1 ? 's' : ''} tâche
									{state.newTasks > 1 ? 's' : ''} créée{state.newTasks > 1 ? 's' : ''}.
								</span>
							)}
						</div>
					)}
				</div>

				{/* ── Utilisateurs card ── */}
				<div className={styles.importCard}>
					<div className={styles.importCardTitle}>Utilisateurs</div>
					<p className={styles.importCardDesc}>
						Colonnes requises : <em>initiales, Nom, Pole, Admin, Actif</em> — séparées par des
						points-virgules.
						<br />
						Colonne optionnelle : <em>email</em>. Si absente ou vide, les nouveaux utilisateurs reçoivent{' '}
						<em>initiales@spirtech.com</em>.<br />
						Colonne <em>Admin</em> : vide ou <em>Admin</em>. Colonne <em>Actif</em> : <em>actif</em> ou{' '}
						<em>inactif</em>.<br />
						Connexion sans mot de passe : un courriel de bienvenue est envoyé à chaque nouvel utilisateur.
					</p>

					<div className={styles.row}>
						<input
							ref={userFileRef}
							type="file"
							accept=".csv,text/csv"
							onChange={(e) => {
								const f = e.target.files?.[0];
								if (f) handleUserFile(f);
							}}
						/>
						{userState.kind !== 'idle' && (
							<button className={styles.toggleBtn} onClick={resetUser}>
								Effacer
							</button>
						)}
					</div>

					{userState.kind === 'validating' && <p className={styles.msg}>Validation…</p>}

					{userState.kind === 'parseError' && (
						<div className={styles.importErrors}>
							<strong>Erreur :</strong> {userState.message}
						</div>
					)}

					{userState.kind === 'errors' && (
						<div className={styles.importErrors}>
							<strong>Import impossible — pôles introuvables :</strong>
							<div className={styles.errorGroup}>
								{userState.unknownTeams.map((n) => (
									<span key={n} className={styles.errorTag}>
										{n}
									</span>
								))}
							</div>
						</div>
					)}

					{userState.kind === 'preview' && (
						<div className={styles.importPreview}>
							{userState.toCreate.length > 0 && (
								<div>
									<span className={styles.previewStat}>{userState.toCreate.length}</span> utilisateur
									{userState.toCreate.length > 1 ? 's' : ''} à créer :
									<span className={styles.previewNote}> {userState.toCreate.join(', ')}</span>
								</div>
							)}
							{userState.toUpdate.length > 0 && (
								<div>
									<span className={styles.previewStat}>{userState.toUpdate.length}</span> utilisateur
									{userState.toUpdate.length > 1 ? 's' : ''} à mettre à jour :
									<span className={styles.previewNote}> {userState.toUpdate.join(', ')}</span>
								</div>
							)}
							{userState.toCreate.length === 0 && userState.toUpdate.length === 0 && (
								<div>Aucune modification détectée.</div>
							)}
							<div className={styles.row} style={{ marginTop: 4 }}>
								<button
									className={styles.importCardBtn}
									onClick={handleUserImport}
									disabled={userState.toCreate.length === 0 && userState.toUpdate.length === 0}
								>
									{userState.toCreate.length === 0 && userState.toUpdate.length === 0
										? 'Rien à importer'
										: 'Importer'}
								</button>
								<button className={styles.btnSecondary} onClick={resetUser}>
									Annuler
								</button>
							</div>
						</div>
					)}

					{userState.kind === 'importing' && <p className={styles.msg}>Importation…</p>}

					{userState.kind === 'done' && (
						<div className={styles.importDone}>
							{userState.created > 0 && (
								<span>
									<strong>{userState.created}</strong> utilisateur{userState.created > 1 ? 's' : ''}{' '}
									créé{userState.created > 1 ? 's' : ''}.{' '}
								</span>
							)}
							{userState.updated > 0 && (
								<span>
									<strong>{userState.updated}</strong> utilisateur{userState.updated > 1 ? 's' : ''}{' '}
									mis à jour.
								</span>
							)}
							{userState.created === 0 && userState.updated === 0 && (
								<span>Aucune modification effectuée.</span>
							)}
						</div>
					)}
				</div>

				{/* ── Projets card ── */}
				<div className={styles.importCard}>
					<div className={styles.importCardTitle}>Projets</div>
					<p className={styles.importCardDesc}>
						Colonnes requises : <em>Pole, Projet, Actif</em> — séparées par des points-virgules.
						<br />
						Colonnes optionnelles : <em>Client, Reference, Categorie, responsables, membres</em>.<br />
						<em>responsables</em> et <em>membres</em> : initiales séparées par des virgules. Les
						responsables voient tous les temps du projet.
					</p>

					<div className={styles.row}>
						<input
							ref={projFileRef}
							type="file"
							accept=".csv,text/csv"
							onChange={(e) => {
								const f = e.target.files?.[0];
								if (f) handleProjFile(f);
							}}
						/>
						{projState.kind !== 'idle' && (
							<button className={styles.toggleBtn} onClick={resetProj}>
								Effacer
							</button>
						)}
					</div>

					{projState.kind === 'validating' && <p className={styles.msg}>Validation…</p>}

					{projState.kind === 'parseError' && (
						<div className={styles.importErrors}>
							<strong>Erreur :</strong> {projState.message}
						</div>
					)}

					{projState.kind === 'errors' && (
						<div className={styles.importErrors}>
							<strong>Import impossible — valeurs introuvables :</strong>
							{projState.unknownTeams.length > 0 && (
								<div className={styles.errorGroup}>
									<span>Pôles inconnus :</span>
									{projState.unknownTeams.map((n) => (
										<span key={n} className={styles.errorTag}>
											{n}
										</span>
									))}
								</div>
							)}
							{projState.unknownInitials.length > 0 && (
								<div className={styles.errorGroup}>
									<span>Initiales inconnues :</span>
									{projState.unknownInitials.map((i) => (
										<span key={i} className={styles.errorTag}>
											{i}
										</span>
									))}
								</div>
							)}
						</div>
					)}

					{projState.kind === 'preview' && (
						<div className={styles.importPreview}>
							{projState.toCreate.length > 0 && (
								<div>
									<span className={styles.previewStat}>{projState.toCreate.length}</span> projet
									{projState.toCreate.length > 1 ? 's' : ''} à créer :
									<span className={styles.previewNote}> {projState.toCreate.join(', ')}</span>
								</div>
							)}
							{projState.toUpdate.length > 0 && (
								<div>
									<span className={styles.previewStat}>{projState.toUpdate.length}</span> projet
									{projState.toUpdate.length > 1 ? 's' : ''} à mettre à jour :
									<span className={styles.previewNote}> {projState.toUpdate.join(', ')}</span>
								</div>
							)}
							{projState.toCreate.length === 0 && projState.toUpdate.length === 0 && (
								<div>Aucune modification détectée.</div>
							)}
							<div className={styles.row} style={{ marginTop: 4 }}>
								<button
									className={styles.importCardBtn}
									onClick={handleProjImport}
									disabled={projState.toCreate.length === 0 && projState.toUpdate.length === 0}
								>
									{projState.toCreate.length === 0 && projState.toUpdate.length === 0
										? 'Rien à importer'
										: 'Importer'}
								</button>
								<button className={styles.btnSecondary} onClick={resetProj}>
									Annuler
								</button>
							</div>
						</div>
					)}

					{projState.kind === 'importing' && <p className={styles.msg}>Importation…</p>}

					{projState.kind === 'done' && (
						<div className={styles.importDone}>
							{projState.created > 0 && (
								<span>
									<strong>{projState.created}</strong> projet{projState.created > 1 ? 's' : ''} créé
									{projState.created > 1 ? 's' : ''}.{' '}
								</span>
							)}
							{projState.updated > 0 && (
								<span>
									<strong>{projState.updated}</strong> projet{projState.updated > 1 ? 's' : ''} mis à
									jour.
								</span>
							)}
							{projState.created === 0 && projState.updated === 0 && (
								<span>Aucune modification effectuée.</span>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

// ── Audit panel ───────────────────────────────────────────────────────────────
// Read-only listing of the 500 most recent admin_audit rows. Each row's payload
// can be expanded to inspect the full JSON.
function AuditPanel() {
	const { entries, loading, error, refresh } = useAudit();
	const [expanded, setExpanded] = useState<Set<number>>(new Set());

	function toggle(id: number) {
		setExpanded((s) => {
			const next = new Set(s);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	function fmtDate(iso: string): string {
		const d = new Date(iso);
		return d.toLocaleString('fr-FR', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});
	}

	if (loading) return <p className={styles.msg}>Chargement…</p>;
	if (error) return <p className={styles.error}>{error}</p>;

	return (
		<div className={styles.panel}>
			<div className={styles.formHeader}>
				<h3 className={styles.formTitle}>Journal d'audit (500 dernières actions)</h3>
				<button className={styles.toggleBtn} onClick={refresh}>
					Rafraîchir
				</button>
			</div>

			<div className={styles.tableWrap}>
				<table className={styles.table}>
					<thead>
						<tr>
							<th>Date</th>
							<th>Auteur</th>
							<th>Action</th>
							<th>Cible</th>
							<th>ID</th>
							<th>Détails</th>
						</tr>
					</thead>
					<tbody>
						{entries.length === 0 && (
							<tr>
								<td colSpan={6} style={{ color: '#a3a3a3' }}>
									Aucune action enregistrée.
								</td>
							</tr>
						)}
						{entries.map((e) => {
							const isOpen = expanded.has(e.id);
							const hasPayload = e.payload != null;
							return (
								<React.Fragment key={e.id}>
									<tr>
										<td style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>
											{fmtDate(e.createdAt)}
										</td>
										<td title={e.userFullName}>{e.userInitials}</td>
										<td style={{ fontFamily: 'monospace', fontSize: 12 }}>{e.action}</td>
										<td>{e.targetType}</td>
										<td>{e.targetId ?? ''}</td>
										<td>
											{hasPayload ? (
												<button className={styles.toggleBtn} onClick={() => toggle(e.id)}>
													{isOpen ? 'Masquer' : 'Afficher'}
												</button>
											) : (
												<span style={{ color: '#a3a3a3' }}>—</span>
											)}
										</td>
									</tr>
									{isOpen && hasPayload && (
										<tr>
											<td colSpan={6} style={{ background: '#fafaf9', padding: '8px 12px' }}>
												<pre
													style={{
														margin: 0,
														fontSize: 12,
														whiteSpace: 'pre-wrap',
														wordBreak: 'break-all',
													}}
												>
													{JSON.stringify(e.payload, null, 2)}
												</pre>
											</td>
										</tr>
									)}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}
